// src/components/VersionWatcher.jsx (Shell)
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '../i18n';
import { useToast } from '../contexts/ToastContext';
import { useVersionCheck } from '../hooks/useVersionCheck';
import { acceptCurrentVersions } from '../utils/versionTracker';
import { reloadPage } from '../utils/reloadPage';
import { createLogger } from '../utils/logger';

const logger = createLogger('Shell::VersionWatcher');

export const RELOAD_COUNTDOWN_S = 30;
// Longer than the toast's 300 ms exit animation, so a Cancel pressed at the last moment
// still unmounts the countdown before it reloads.
export const CANCEL_GRACE_MS = 400;

/**
 * Toast body: counts down, then calls `onElapsed`. Closing the toast unmounts it, which
 * stops the countdown; `onUnmount` fires however the toast went away.
 */
function ReloadCountdown({ seconds, onElapsed, onUnmount }) {
    const { t } = useTranslation('shell');
    const [left, setLeft] = useState(seconds);

    useEffect(() => {
        const deadline = Date.now() + seconds * 1000;
        let elapsedTimer;
        const timer = setInterval(() => {
            const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
            setLeft(remaining);
            if (remaining === 0) {
                clearInterval(timer);
                elapsedTimer = setTimeout(onElapsed, CANCEL_GRACE_MS);
            }
        }, 1000);
        return () => {
            clearInterval(timer);
            clearTimeout(elapsedTimer);
        };
    }, [seconds, onElapsed]);

    useEffect(() => onUnmount, [onUnmount]);

    return t('versionWatcher.reloadingIn', { seconds: left });
}

/**
 * When a new build is live, shows a countdown toast and reloads the page when it ends.
 * Cancel keeps this tab on its current build until the next deploy. Renders nothing itself.
 */
export function VersionWatcher() {
    const { addToast } = useToast();
    const { t } = useTranslation('shell');
    const promptOpen = useRef(false);

    // However the toast went away (Cancel, or an MFE clearing all toasts), allow the next prompt.
    const handlePromptGone = useCallback(() => {
        promptOpen.current = false;
    }, []);

    const handleNewVersion = useCallback(() => {
        if (promptOpen.current) return;
        promptOpen.current = true;
        logger.info(`New version deployed — reloading in ${RELOAD_COUNTDOWN_S}s unless cancelled`);

        addToast({
            message: (
                <ReloadCountdown
                    seconds={RELOAD_COUNTDOWN_S}
                    onElapsed={reloadPage}
                    onUnmount={handlePromptGone}
                />
            ),
            type: 'info',
            duration: 0,
            source: 'version-watcher',
            closeLabel: t('versionWatcher.cancel'),
            onClose: () => {
                acceptCurrentVersions();
                logger.info('Reload cancelled — staying on this version until the next deploy');
            },
        });
    }, [addToast, t, handlePromptGone]);

    useVersionCheck(handleNewVersion);

    return null;
}
